import { createStore } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'

import { sessionStore } from '@gto/session'

import type { Account, TokenPair } from './api/gen'
import { accountRetrieve } from './api/gen'

export type Store = {
  user: Account | null
  isAuthorized: boolean
  emailValidated: boolean
  isInitialized: boolean
  termsAccepted: boolean

  onLogin: (creds: TokenPair, cb?: () => void) => Promise<void>
  onLogout: (withRedirect?: boolean) => void
  setEmailValidated: (value: boolean) => void
  setTermsAccepted: (value: boolean) => void
  initialize: () => Promise<void>
  refetchUser: () => Promise<void>
  updateBalance: (payload: string) => void
}

export const store = createStore<Store>()(
  subscribeWithSelector((set, get) => ({
    user: null,
    isAuthorized: false,
    emailValidated: false,
    isInitialized: false,
    termsAccepted: true,
    error: null,

    onLogin: async (data: TokenPair, cb?: () => void) => {
      const token = data.access
      if (!token) {
        return
      }

      try {
        sessionStore.getState().setAccessToken(token)
        localStorage.setItem('user_refresh', data.refresh)

        await get().refetchUser()
        cb?.()
      } catch (error) {
        console.error('Login failed:', error)
      }
    },

    onLogout: (withRedirect = true) => {
      sessionStore.getState().clearAccessToken()
      localStorage.removeItem('user_refresh')
      localStorage.removeItem('termsAccepted')
      if (withRedirect) {
        window.location.href = '/'
      }

      set({
        user: null,
        isAuthorized: false,
        emailValidated: false,
        termsAccepted: true,
      })
    },

    setEmailValidated: (value: boolean) => {
      set({ emailValidated: value })
    },

    setTermsAccepted: (value: boolean) => {
      localStorage.setItem('termsAccepted', String(value))
      set({ termsAccepted: value })
    },

    updateBalance: (payload: string) => {
      const oldUser = get().user
      if (!oldUser) {
        return
      }

      const updatedUser = {
        ...oldUser,
        edge_balance: payload,
      }
      set({
        user: updatedUser,
      })
    },
    initialize: async () => {
      const token = sessionStore.getState().accessToken
      const refreshToken = localStorage.getItem('user_refresh')

      if (token === null && refreshToken === null) {
        set({ isInitialized: true })

        return
      }

      try {
        await get().refetchUser()
      } catch (error) {
        console.error('Auth initialization failed:', error)
        set({
          isAuthorized: false,
          user: null,
        })
      } finally {
        set({ isInitialized: true })
      }
    },

    refetchUser: async () => {
      try {
        const account = await accountRetrieve()
        const termsAccepted = account.term_and_condition === true

        set({
          user: account,
          isAuthorized: true,
          emailValidated: account.email_verified,
          isInitialized: true,
          termsAccepted,
        })
      } catch (error) {
        console.error('Failed to fetch user account', error)
        set({
          user: null,
          isAuthorized: false,
          emailValidated: false,
          termsAccepted: true,
        })
      }
    },
  })),
)
